import { historicalMeetingRecords } from './HistoricalMeetings';
import { realTimeMeetingRecords } from './RealTimeMeetings';

export type MeetingSource = 'real-time' | 'historical';
export type QualityStatus = '好' | '一般' | '—';

export interface MeetingQuality {
  status: QualityStatus;
  score?: number;
}

type RealTimeMeeting = (typeof realTimeMeetingRecords)[number];
type HistoricalMeeting = (typeof historicalMeetingRecords)[number];
export type MeetingRecord = RealTimeMeeting | HistoricalMeeting;

export interface ParticipantRow {
  id: string;
  name: string;
  account: string;
  email: string;
  isMeetingRoomDevice: boolean;
  networkType: string;
  ipAddress: string;
  province: string;
  operator: string;
  overall: MeetingQuality;
  audio: MeetingQuality;
  video: MeetingQuality;
  screen: MeetingQuality;
  network: MeetingQuality;
  audioStutterRate: number | null;
  audioStutterCount: number | null;
  videoStutterRate: number | null;
  videoStutterCount: number | null;
  screenStutterRate: number | null;
  screenStutterCount: number | null;
  avgLatencyMs: number | null;
  reconnectCount: number | null;
  device: string;
  microphone: string;
  speaker: string;
  camera: string;
  joinTime: string;
  leaveTime: string;
}

export const getParticipantDeviceLabel = (participant: ParticipantRow) =>
  participant.isMeetingRoomDevice ? '会议室' : participant.device;

const pad2 = (value: number) => `${value}`.padStart(2, '0');

const formatDateTime = (date: Date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(
    date.getHours()
  )}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;

export const shiftTime = (time: string, minutes: number) => {
  const parsed = new Date(time.replace(/-/g, '/'));
  if (Number.isNaN(parsed.getTime())) {
    return time;
  }
  parsed.setMinutes(parsed.getMinutes() + minutes);
  return formatDateTime(parsed);
};

const getQualityByIndex = (quality: MeetingQuality, rowIndex: number): MeetingQuality => {
  if (rowIndex === 0) {
    return quality;
  }
  if (rowIndex % 3 === 0) {
    return { status: '—' };
  }
  return quality;
};

export const getMeetingBySource = (source: MeetingSource, meetingId: string) => {
  const list = source === 'historical' ? historicalMeetingRecords : realTimeMeetingRecords;
  return list.find(item => item.id === meetingId);
};

export const buildParticipantRows = (meeting: MeetingRecord, source: MeetingSource) => {
  const seedNames = ['技术支持', '主持人', '记录员', '观察员', '产品经理', '测试用户'];
  const seedProvinces = ['北京', '上海', '广东', '浙江', '四川', '山东'];
  const seedOperators = ['中国移动', '中国联通', '中国电信', '教育网', '企业专线', '广电网络'];
  const meetingRoomNames = [
    '成都分部-天府厅',
    '重庆客户支持-501',
    '大连研发组-3F',
    '福州项目组-协作室',
    '杭州客户中心-3号室',
    '济南区域中心-开会间'
  ];
  const seedDevices = [
    'iOS（iphone17/iOS26.4）',
    '安卓（小米17/Android 16）',
    'web（Windows 10/Chrome 138）',
    'iOS（iphone16 Pro/iOS26.3）',
    '鸿蒙（华为mate70/HarmonyOS 6.0）',
    'Mac（macOS26.3）',
    'web（macOS 15/Safari 18）',
    'windows（Windows 11 24H2）'
  ];
  const participantCount = Math.max(meeting.participants, 1);
  const isHistorical = source === 'historical';
  const shouldForceLastAsMeetingRoom =
    (source === 'real-time' && meeting.id === realTimeMeetingRecords[0]?.id) ||
    (source === 'historical' && meeting.id === historicalMeetingRecords[0]?.id);
  const firstEmail = meeting.account.includes('@') ? meeting.account : `${meeting.account}@360.cn`;
  const endTime = 'endTime' in meeting ? meeting.endTime : '—';

  const rows: ParticipantRow[] = [];
  for (let index = 0; index < participantCount; index += 1) {
    const isFirst = index === 0;
    const isForcedMeetingRoom = shouldForceLastAsMeetingRoom && index === participantCount - 1;
    const isMeetingRoomDevice = !isFirst && (index % 3 === 0 || isForcedMeetingRoom);
    const account = isFirst ? meeting.account : `${meeting.account}-${index}`;
    const participantName = isMeetingRoomDevice
      ? meetingRoomNames[index % meetingRoomNames.length]
      : (isFirst ? meeting.creator : `${meeting.creator}${seedNames[index % seedNames.length]}`);
    rows.push({
      id: `${meeting.id}-${index}`,
      name: participantName,
      account,
      email: isFirst ? firstEmail : `${account}@360.cn`,
      isMeetingRoomDevice,
      networkType: index % 2 === 0 ? 'Wi-Fi' : '4g',
      ipAddress: `10.${20 + index}.${15 + index}.${80 + index}`,
      province: seedProvinces[index % seedProvinces.length],
      operator: seedOperators[index % seedOperators.length],
      overall: getQualityByIndex(meeting.overall, index),
      audio: getQualityByIndex(meeting.audio, index),
      video: getQualityByIndex(meeting.video, index),
      screen: getQualityByIndex(meeting.screen, index),
      network: getQualityByIndex(meeting.network, index),
      audioStutterRate: meeting.audioStutterRate === null ? null : Number((meeting.audioStutterRate + index * 0.08).toFixed(2)),
      audioStutterCount: meeting.audioStutterCount === null ? null : meeting.audioStutterCount + (index % 2),
      videoStutterRate: meeting.videoStutterRate === null ? null : Number((meeting.videoStutterRate + index * 0.06).toFixed(2)),
      videoStutterCount: meeting.videoStutterCount === null ? null : meeting.videoStutterCount + (index % 2),
      screenStutterRate: meeting.screenStutterRate === null ? null : Number((meeting.screenStutterRate + index * 0.05).toFixed(2)),
      screenStutterCount: meeting.screenStutterCount === null ? null : meeting.screenStutterCount + (index % 2),
      avgLatencyMs: meeting.avgLatencyMs === null ? null : Number((meeting.avgLatencyMs / Math.max(index + 1, 1)).toFixed(2)),
      reconnectCount: meeting.reconnectCount === null ? null : Number(Math.max(0, meeting.reconnectCount - index * 0.2).toFixed(1)),
      device: isMeetingRoomDevice ? 'RoomOS' : seedDevices[index % seedDevices.length],
      microphone: '默认设备',
      speaker: '默认设备',
      camera: index % 2 === 0 ? '内置摄像头' : '—',
      joinTime: shiftTime(meeting.startTime, index * 2),
      leaveTime: isHistorical ? (isFirst ? endTime : shiftTime(meeting.startTime, 30 + index * 2)) : '—'
    });
  }
  return rows;
};
